import React from 'react';
import { Mail, MapPin, Phone } from 'lucide-react';
import { Button } from '../components/ui/button';
import Layout from '../components/layout/Layout';
import { useLanguage } from '../i18n';

const ContactPage = () => {
  const { t, getProfile, getContact } = useLanguage();
  const profile = getProfile();
  const contact = getContact();

  return (
    <Layout>
      <section className="py-12 lg:py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="mb-12 lg:mb-16">
            <span className="label-mono mb-4 block">{t('contact.label')}</span>
            <h1 className="section-title">{t('contact.title')}</h1>
            <p className="font-body text-lg text-slate-600 mt-4 max-w-2xl">
              {t('contact.description')}
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
            <div>
              <div className="bg-white border border-slate-200 p-8 lg:p-10 mb-8">
                <h2 className="font-display text-xl font-medium text-slate-800 mb-4">{t('contact.aboutMe')}</h2>
                <div className="font-body text-slate-600 leading-relaxed text-justify space-y-4">
                  {t('contact.aboutText').split('\n\n').map((paragraph, index) => (
                    <p key={index}>{paragraph}</p>
                  ))}
                </div>
              </div>
              
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-50 border border-emerald-200">
                <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                <span className="font-mono text-xs uppercase tracking-wider text-emerald-600">{t('contact.openToOpportunities')}</span>
              </div>
            </div>

            <div className="bg-white border border-slate-200 p-8 lg:p-10">
              <h2 className="font-display text-xl font-medium text-slate-800 mb-6">{t('contact.contactInfo')}</h2>
              
              <div className="space-y-6">
                <a href={`mailto:${contact.email}`} className="group flex items-start gap-4 p-4 -m-4 hover:bg-blue-50 transition-colors">
                  <div className="w-12 h-12 flex items-center justify-center bg-blue-50 group-hover:bg-blue-600 transition-colors">
                    <Mail className="w-5 h-5 text-blue-600 group-hover:text-white transition-colors" />
                  </div>
                  <div>
                    <span className="font-mono text-xs uppercase tracking-wider text-slate-500 block mb-1">{t('contact.email')}</span>
                    <span className="font-body text-slate-800 group-hover:text-blue-600">{contact.email}</span>
                  </div>
                </a>

                <a href={`tel:${contact.phone}`} className="group flex items-start gap-4 p-4 -m-4 hover:bg-blue-50 transition-colors">
                  <div className="w-12 h-12 flex items-center justify-center bg-blue-50 group-hover:bg-blue-600 transition-colors">
                    <Phone className="w-5 h-5 text-blue-600 group-hover:text-white transition-colors" />
                  </div>
                  <div>
                    <span className="font-mono text-xs uppercase tracking-wider text-slate-500 block mb-1">{t('contact.phone')}</span>
                    <span className="font-body text-slate-800 group-hover:text-blue-600">{contact.phone}</span>
                  </div>
                </a>

                <div className="flex items-start gap-4 p-4 -m-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-blue-50">
                    <MapPin className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <span className="font-mono text-xs uppercase tracking-wider text-slate-500 block mb-1">{t('contact.location')}</span>
                    <span className="font-body text-slate-800">{contact.location}</span>
                  </div>
                </div>
              </div>

              <div className="mt-8 pt-6 border-t border-slate-200">
                <Button asChild className="w-full font-mono text-xs uppercase tracking-wider rounded-none bg-blue-600 hover:bg-blue-700 text-white py-5">
                  <a href={`mailto:${contact.email}`}>
                    <Mail className="w-4 h-4 mr-2" />{t('contact.sendEmail')}
                  </a>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default ContactPage;
